import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, CheckCircle2, AlertCircle, Building2, LineChart, Bot, Cog, ScrollText } from "lucide-react";

const CATEGORIES = {
  brokers: ["alpaca", "ibkr", "tradier", "tradestation", "mock"],
  market_data: ["coingecko", "alpha_vantage", "polygon", "alpaca_md"],
  ai: ["claude", "openai", "gemini"],
  jobs: [],   // populated dynamically from job_runs
};

function fmtAgo(sec) {
  if (sec == null) return "never";
  if (sec < 60) return `${Math.round(sec)}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function ProviderCard({ p, testid }) {
  const healthy = p.healthy;
  const errorRate = p.calls > 0 ? Math.round((p.errors / p.calls) * 100) : 0;
  return (
    <Card className={`border rounded-2xl p-5 ${healthy ? "border-gray-200" : "border-red-300 bg-red-50/40"}`} data-testid={testid || `provider-card-${p.provider}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {healthy ? <CheckCircle2 size={18} className="text-green-600" /> : <AlertCircle size={18} className="text-red-600" />}
          <p className="font-medium font-mono">{p.provider}</p>
        </div>
        <Badge className={healthy ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
          {healthy ? "healthy" : "degraded"}
        </Badge>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div><p className="text-[10px] font-bold uppercase text-gray-500">Calls</p><p className="font-mono tabular-nums text-lg">{p.calls}</p></div>
        <div><p className="text-[10px] font-bold uppercase text-gray-500">Errors</p><p className={`font-mono tabular-nums text-lg ${errorRate > 20 ? "text-red-600" : ""}`}>{p.errors} ({errorRate}%)</p></div>
        <div><p className="text-[10px] font-bold uppercase text-gray-500">Last OK</p><p className="font-mono tabular-nums text-lg">{fmtAgo(p.age_since_success_sec)}</p></div>
      </div>
      {p.last_endpoint && <p className="mt-3 text-xs text-gray-500">Last: <span className="font-mono">{p.last_endpoint}</span></p>}
      {p.last_error_msg && !healthy && <p className="mt-2 text-xs text-red-700 font-mono truncate" title={p.last_error_msg}>{p.last_error_msg}</p>}
    </Card>
  );
}

function EmptyState({ children }) {
  return <Card className="border border-dashed border-gray-300 rounded-2xl p-10 text-center text-sm text-gray-500">{children}</Card>;
}

export default function Health() {
  const [providers, setProviders] = useState([]);
  const [features, setFeatures] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    const load = () => {
      api.get("/health/providers").then(({ data }) => setProviders(data.providers || []));
      api.get("/config/features").then(({ data }) => setFeatures(data.flags));
      api.get("/audit/mine?limit=100").then(({ data }) => setAudit(data)).catch(() => {});
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const byProvider = Object.fromEntries(providers.map((p) => [p.provider, p]));
  const inCategory = (cat) => CATEGORIES[cat].map((n) => byProvider[n]).filter(Boolean);
  const brokers = inCategory("brokers");
  const marketData = inCategory("market_data");
  const aiAgents = providers.filter((p) => p.provider.includes("claude") || p.provider.includes("openai") || p.provider.includes("gemini") || p.provider.includes("emergent"));
  const stripe = byProvider["stripe"];
  const otherProviders = providers.filter((p) =>
    !CATEGORIES.brokers.includes(p.provider) &&
    !CATEGORIES.market_data.includes(p.provider) &&
    !aiAgents.includes(p) &&
    p.provider !== "stripe"
  );

  return (
    <div className="space-y-8" data-testid="health-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Ops</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2 flex items-center gap-3">
          <Activity size={40} />Health &amp; Audit
        </h1>
        <p className="text-gray-500 mt-3">Live telemetry across every third-party dependency. Refreshes every 10s.</p>
      </div>

      {features && (
        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="feature-flags-card">
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-3">Feature flags</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(features).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs" data-testid={`flag-${k}`}>
                <Badge className={v ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}>{v ? "on" : "off"}</Badge>
                <span className="font-mono">{k}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Tabs defaultValue="brokers" className="w-full" data-testid="health-tabs">
        <TabsList className="bg-gray-100 h-11">
          <TabsTrigger value="brokers" data-testid="tab-brokers" className="gap-2 px-3"><Building2 size={14} />Brokers</TabsTrigger>
          <TabsTrigger value="market" data-testid="tab-market" className="gap-2 px-3"><LineChart size={14} />Market Data</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai" className="gap-2 px-3"><Bot size={14} />AI Agents</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs" className="gap-2 px-3"><Cog size={14} />Jobs</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" className="gap-2 px-3"><ScrollText size={14} />Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="brokers" className="mt-6 space-y-4">
          {brokers.length === 0 ? <EmptyState>No broker calls yet. Connect a broker or open /broker.</EmptyState> :
            <div className="grid md:grid-cols-2 gap-4">{brokers.map((p) => <ProviderCard key={p.provider} p={p} testid={`broker-health-${p.provider}`} />)}</div>}
          {stripe && <div className="grid md:grid-cols-2 gap-4"><ProviderCard p={stripe} testid="broker-health-stripe" /></div>}
        </TabsContent>

        <TabsContent value="market" className="mt-6 space-y-4">
          {marketData.length === 0 ? <EmptyState>No market data calls yet. Open /markets to warm the providers.</EmptyState> :
            <div className="grid md:grid-cols-2 gap-4">{marketData.map((p) => <ProviderCard key={p.provider} p={p} testid={`market-health-${p.provider}`} />)}</div>}
        </TabsContent>

        <TabsContent value="ai" className="mt-6 space-y-4">
          {aiAgents.length === 0 ? <EmptyState>No AI calls yet. Grade a trade or run the AI Council.</EmptyState> :
            <div className="grid md:grid-cols-2 gap-4">{aiAgents.map((p) => <ProviderCard key={p.provider} p={p} testid={`ai-health-${p.provider}`} />)}</div>}
          {otherProviders.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">{otherProviders.map((p) => <ProviderCard key={p.provider} p={p} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          <Card className="border border-gray-200 rounded-2xl p-6" data-testid="jobs-card">
            <p className="text-sm text-gray-500 leading-relaxed">
              Background jobs (auto-execution tick, weekly digest batch, order/alert evaluators) run continuously.
              Job-run records are written to <code className="font-mono text-xs">job_runs</code> on each schedule tick.
              Detailed per-job telemetry lands in <a href="/audit" data-testid="audit-page-link" className="underline">Audit</a>.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <Card className="border border-gray-200 rounded-2xl overflow-hidden" data-testid="audit-card">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <p className="font-medium">Your recent audit events</p>
              <span className="text-xs text-gray-500">Latest 100</span>
            </div>
            {audit.length === 0 ? <p className="text-sm text-gray-500 p-6">No events yet.</p> : (
              <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {audit.map((a) => (
                  <div key={a._id} className="px-6 py-3 text-xs flex items-center gap-3" data-testid={`audit-row-${a._id}`}>
                    <span className="font-mono text-gray-400 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                    <Badge className={
                      a.outcome === "reject" ? "bg-red-100 text-red-700" :
                      a.outcome === "error" ? "bg-orange-100 text-orange-700" :
                      a.outcome === "skip" ? "bg-gray-100 text-gray-600" :
                      a.severity === "warn" ? "bg-yellow-100 text-yellow-700" :
                      "bg-green-100 text-green-700"
                    }>{a.kind}</Badge>
                    <span className="text-gray-600 truncate flex-1">
                      {a.context?.reason || a.context?.symbol || a.context?.rule || JSON.stringify(a.context).slice(0, 120)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
